/**
 * AIChatWidget — Floating AI Chat component
 *
 * Renders a chat bubble that opens a conversation panel.
 * Uses React Query for server state and integrates with the AI chat API.
 *
 * Security:
 *   - PII redaction happens server-side (not here)
 *   - Responses are rendered as sanitized text (no dangerouslySetInnerHTML)
 *   - Entitlement gating enforced by the AI router at runtime
 *
 * Gating contract (from AI router):
 *   - FEATURE_DISABLED: global kill switch (403)
 *   - AI_CREDITS_EXHAUSTED: quota exhausted (403 + data.usage)
 *   - AI_REQUESTS_EXHAUSTED: request limit hit (403 + data.usage)
 *   - AI_PROJECTION_FAILED: projection error (403)
 *   - AI_ADVANCED_NOT_ENABLED: advanced tier denied (403)
 *   - RATE_LIMITED: per-user rate limit (429)
 */

import { useState, useRef, useEffect, useCallback, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

// --- Types ---

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model?: string | null;
  createdAt: string;
  /** Marks an optimistic user message that failed to send */
  failed?: boolean;
  /** Original text for retry */
  originalText?: string;
}

interface AIUsageInfo {
  creditsUsed: number;
  creditsLimit: number | null;
  creditsRemaining: number | null;
  requestsUsed: number;
  requestsLimit: number | null;
  requestsRemaining: number | null;
}

interface ChatResponse {
  success: boolean;
  data: {
    conversationId: string;
    message: ChatMessage;
    usage?: AIUsageInfo;
  };
}

interface ErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
  };
  data?: {
    usage?: AIUsageInfo;
  };
}

/** Denial codes that represent persistent blocked states (input should be disabled) */
const PERSISTENT_DENIAL_CODES = new Set([
  'FEATURE_DISABLED',
  'AI_CREDITS_EXHAUSTED',
  'AI_REQUESTS_EXHAUSTED',
]);

/** Human-readable messages for denial codes */
const DENIAL_MESSAGES: Record<string, string> = {
  FEATURE_DISABLED: 'AI chat is not enabled for this workspace.',
  AI_CREDITS_EXHAUSTED: 'AI credits exhausted for this billing period.',
  AI_REQUESTS_EXHAUSTED: 'Monthly AI request limit reached.',
  AI_PROJECTION_FAILED: 'Service temporarily unavailable. Please try again.',
  AI_ADVANCED_NOT_ENABLED: 'Advanced AI is not available on your plan.',
  RATE_LIMITED: 'Please wait before sending another message.',
};

/**
 * Extract a structured error response from an axios error.
 * Returns null if the error shape doesn't match.
 */
function extractErrorResponse(err: unknown): ErrorResponse | null {
  if (
    err &&
    typeof err === 'object' &&
    'response' in err &&
    (err as { response?: { data?: ErrorResponse } }).response?.data?.error?.code
  ) {
    return (err as { response: { data: ErrorResponse } }).response.data;
  }
  return null;
}

// --- Component ---

export function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [usage, setUsage] = useState<AIUsageInfo | null>(null);
  const [denialState, setDenialState] = useState<{
    code: string;
    message: string;
    usage?: AIUsageInfo;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();
  const queryClient = useQueryClient();

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !denialState) {
      inputRef.current?.focus();
    }
  }, [isOpen, denialState]);

  // Load conversation messages when conversationId changes
  const { data: existingMessages } = useQuery({
    queryKey: ['ai-chat-messages', conversationId],
    queryFn: async () => {
      if (!conversationId) return [];
      const response = await api.get<{ data: ChatMessage[] }>(
        `/ai/conversations/${conversationId}/messages`,
      );
      return response.data.data;
    },
    enabled: !!conversationId && isOpen,
  });

  // Sync existing messages
  useEffect(() => {
    if (existingMessages?.length) {
      setMessages(existingMessages);
    }
  }, [existingMessages]);

  // Send message mutation
  const sendMessage = useMutation({
    mutationFn: async (message: string) => {
      const response = await api.post<ChatResponse>('/ai/chat', {
        message,
        conversationId: conversationId ?? undefined,
        pageContext: location.pathname,
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (!conversationId) {
        setConversationId(data.data.conversationId);
      }
      // Remove the optimistic user message (temp-*) and replace with server response
      setMessages((prev) => {
        const withoutOptimistic = prev.filter(
          (m) => !(m.role === 'user' && m.id.startsWith('temp-')),
        );
        // The server echoes the user message in the conversation, so we add the assistant reply
        return [...withoutOptimistic, data.data.message];
      });
      // Update usage from successful response
      if (data.data.usage) {
        setUsage(data.data.usage);
      }
      // Clear any transient denial (e.g. rate limit)
      if (denialState && !PERSISTENT_DENIAL_CODES.has(denialState.code)) {
        setDenialState(null);
      }
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
    onError: (err: unknown) => {
      const errorResp = extractErrorResponse(err);
      const code = errorResp?.error?.code;

      if (code && DENIAL_MESSAGES[code]) {
        // Set denial state
        setDenialState({
          code,
          message: DENIAL_MESSAGES[code],
          usage: errorResp?.data?.usage,
        });

        // For persistent denials, remove the optimistic message entirely
        // (there's nothing the user can do in-chat to fix it)
        if (PERSISTENT_DENIAL_CODES.has(code)) {
          setMessages((prev) =>
            prev.filter((m) => !(m.role === 'user' && m.id.startsWith('temp-'))),
          );
          return;
        }
      }

      // Mark optimistic message as failed (for transient errors / rate limit / network)
      setMessages((prev) =>
        prev.map((m) =>
          m.role === 'user' && m.id.startsWith('temp-') && !m.failed
            ? { ...m, failed: true, originalText: m.content }
            : m,
        ),
      );
    },
  });

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const trimmed = input.trim();
      if (!trimmed || sendMessage.isPending || (denialState && PERSISTENT_DENIAL_CODES.has(denialState.code))) return;

      // Clear any stale failed messages before sending a new one
      setMessages((prev) => prev.filter((m) => !m.failed));

      // Clear transient denial state
      if (denialState && !PERSISTENT_DENIAL_CODES.has(denialState.code)) {
        setDenialState(null);
      }

      // Optimistically add user message
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setInput('');

      sendMessage.mutate(trimmed);
    },
    [input, sendMessage, denialState],
  );

  const handleRetry = useCallback(
    (msg: ChatMessage) => {
      if (!msg.originalText || sendMessage.isPending) return;

      // Remove the failed message and re-send
      const text = msg.originalText;
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));

      // Clear transient denial
      if (denialState && !PERSISTENT_DENIAL_CODES.has(denialState.code)) {
        setDenialState(null);
      }

      // Add fresh optimistic message
      const retryMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, retryMessage]);
      sendMessage.mutate(text);
    },
    [sendMessage, denialState],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e as unknown as FormEvent);
      }
    },
    [handleSubmit],
  );

  const handleNewConversation = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setDenialState(null);
    setUsage(null);
  }, []);

  const isInputDisabled =
    sendMessage.isPending ||
    (denialState !== null && PERSISTENT_DENIAL_CODES.has(denialState.code));

  return (
    <>
      {/* Chat toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition-transform hover:scale-105 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
        aria-label={isOpen ? 'Close AI assistant' : 'Open AI assistant'}
      >
        {isOpen ? (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 flex h-[500px] w-[380px] flex-col rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full ${denialState && PERSISTENT_DENIAL_CODES.has(denialState.code) ? 'bg-gray-400' : 'bg-green-500'}`} />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                AMuster Assistant
              </h3>
            </div>
            <div className="flex items-center gap-1">
              {usage && usage.creditsLimit !== null && (
                <span className="text-xs text-gray-400">
                  {usage.creditsRemaining ?? 0}/{usage.creditsLimit} credits
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewConversation}
                className="text-xs"
              >
                New chat
              </Button>
            </div>
          </div>

          {/* Persistent denial state card */}
          {denialState && PERSISTENT_DENIAL_CODES.has(denialState.code) && (
            <div className="mx-4 mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
              <p className="text-sm font-medium text-amber-800">{denialState.message}</p>
              {denialState.usage && denialState.usage.creditsLimit !== null && (
                <p className="mt-1 text-xs text-amber-600">
                  {denialState.usage.creditsUsed}/{denialState.usage.creditsLimit} credits used
                </p>
              )}
              {denialState.code === 'FEATURE_DISABLED' && (
                <p className="mt-1 text-xs text-amber-600">
                  Contact your platform administrator to enable AI chat.
                </p>
              )}
            </div>
          )}

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && !denialState && (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Ask me anything about AMuster
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className="max-w-[85%]">
                  <div
                    className={`rounded-lg px-3 py-2 text-sm ${
                      msg.role === 'user'
                        ? msg.failed
                          ? 'border border-red-300 bg-red-50 text-red-800'
                          : 'bg-brand-600 text-white'
                        : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                    }`}
                  >
                    {/* Render as text only — no HTML injection */}
                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                  </div>
                  {/* Failed message retry action */}
                  {msg.failed && (
                    <button
                      onClick={() => handleRetry(msg)}
                      disabled={sendMessage.isPending}
                      className="mt-1 text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                    >
                      Failed to send — tap to retry
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sendMessage.isPending && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-500 dark:bg-gray-800">
                  <span className="animate-pulse">Thinking...</span>
                </div>
              </div>
            )}
            {/* Transient denial message (rate limit, projection failed) */}
            {denialState && !PERSISTENT_DENIAL_CODES.has(denialState.code) && (
              <div className="flex justify-center">
                <p className="text-xs text-amber-600">{denialState.message}</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <form
            onSubmit={handleSubmit}
            className="border-t border-gray-200 px-4 py-3 dark:border-gray-700"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isInputDisabled && denialState ? denialState.message : 'Type a message...'}
                rows={1}
                className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                disabled={isInputDisabled}
                maxLength={4000}
              />
              <Button
                type="submit"
                size="sm"
                disabled={!input.trim() || isInputDisabled}
                className="shrink-0"
              >
                Send
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
